import React, { useEffect, useState } from 'react';
import { Input, Button, Form, Upload, Typography, Space, Row, Col, message, Select, List, Progress, Alert, Spin } from 'antd';
import { UploadOutlined, UserOutlined, SoundOutlined, PhoneOutlined, LinkOutlined, PlayCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Navigate, useLocation } from 'react-router-dom';
import { auth } from '../../App';
import { useDispatch, useSelector } from 'react-redux';
import { talentSearchSubmissions, uploadFile } from '../../actions/TalentSearchActions';
import { logPageVisit } from '../../actions/AnalyticsActions';
import { BiMicrophone, BiMicrophoneOff, BiUpload } from 'react-icons/bi';
import { setLanguage } from '../../actions/LanguageActions';
import { Helmet } from 'react-helmet';

const { Title, Text } = Typography;
const { Option } = Select;

const TalentSearch = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [form] = Form.useForm();
  const [personalPic, setPersonalPic] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [recordings, setRecordings] = useState([]); // To store recordings
  const [recordingIndex, setRecordingIndex] = useState(1);

  const [uploading, setUploading] = useState(false);

  // Progress state for uploads
  const [uploadProgress, setUploadProgress] = useState({
    personalPic: 0,
    mediaFiles: {},
    recordings: {},
  });

  const { submitting, success, error } = useSelector(state => state.talentSearch);
  const { translations, language } = useSelector((state) => state.language);
  const { entry } = useSelector((state) => state.analytics);

  const isSwahili = location.pathname.includes("/kipaji");


  useEffect(() => {

    if (entry === 0) {
      // console.log("User entered the app directly on:", location.pathname);
      if (location.pathname === "/talent") {
        dispatch(setLanguage('en'));
      } else if (location.pathname === "/kipaji") {
        dispatch(setLanguage('sw'));
      }
    } else {
      // console.log("User navigated within the app to:", location.pathname);
    }
  }, [entry]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(logPageVisit("Talent Search"));
  }, [dispatch]);


  // Effect to clear fields after successful submission
  useEffect(() => {
    if (success) {
      // Clear local states
      setPersonalPic(null);
      setMediaFiles([]);
      setRecordings([]);
      setUploadProgress({
        personalPic: 0,
        mediaFiles: {},
        recordings: {},
      });
      form.resetFields();

      // Optionally show a success message
      // message.success("Submission successful! Fields have been cleared.");
    }
  }, [success, form]);

  const { status, startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({
    audio: true,
    onStop: (blobUrl, blob) => {
      // Create the new recording object immediately when recording stops
      const newRecording = {
        link: blobUrl, // Blob URL for playback
        file: blob, // Raw blob for upload
        title: `${translations.talent.recording} ${recordingIndex}`, // Default description
        description: `${translations.talent.recordingDescription} ${recordingIndex}`, // Default description
      };

      // Add the new recording to the list
      setRecordings((prev) => [...prev, newRecording]);
      setRecordingIndex(recordingIndex + 1);
    },
  });

  const handleStartRecording = (index) => {
    if (recordings.length > 4) {
      message.error(`${translations.talent.maxRecords}`);
      return; // Ignore invalid files
    }
    startRecording();
  };

  // Remove recording from the list
  const handleRemoveRecording = (index) => {
    const updatedRecordings = recordings.filter((_, i) => i !== index);
    setRecordings(updatedRecordings);
  };

  // Handle description change
  const handleRecordingDescriptionChange = (index, value) => {
    const updatedRecordings = [...recordings];
    updatedRecordings[index].description = value;
    setRecordings(updatedRecordings);
  };


  // const onPersonalPicChange = (info) => {
  //   if (info.file.status === 'done') {
  //     message.success(`${info.file.name} file uploaded successfully`);
  //     setPersonalPic(info.file.originFileObj);
  //   } else if (info.file.status === 'error') {
  //     console.log(info.file)
  //     message.error(`${info.file.name} file upload failed.`);
  //   }
  // };

  const onPersonalPicChange = ({ file }) => {
    if (file && file.type.startsWith("image/")) {
      setPersonalPic(file); // Save the selected file to state
    } else {
      message.error(`${translations.talent.validImage}`);
    }
  };

  // const onMediaChange = ({ file, fileList }) => {
  //   if (file.status === 'done') {
  //     const newFile = {
  //       link: URL.createObjectURL(file.originFileObj),
  //       description: file.name,
  //     };
  //     setMediaFiles((prevFiles) => [...prevFiles, newFile]);
  //   } else if (file.status === 'error') {
  //     message.error(`${file.name} file upload failed.`);
  //   }
  // };

  // const handleFileUpload = (file) => {
  //   const isAudio = file.type === 'audio/mpeg' || file.type === 'audio/wav';
  //   const isVideo = file.type === 'video/mp4' || file.type === 'video/quicktime';

  //   if (!isAudio && !isVideo) {
  //     message.error('You can only upload audio or video files!');
  //     return Upload.LIST_IGNORE;
  //   }
  //   return true;
  // };


  // const onMediaChange = ({ file, fileList }) => {
  //   if (file.status === "done" || file.status === "removed") {
  //     setMediaFiles(fileList.map((file) => ({
  //       link: file.uid, // Generate unique ID
  //       description: file.name || "New Media File", // Default description
  //       file: file.originFileObj, // Store raw file object
  //     })));
  //   }
  // };

  // const handleFileUpload = (file) => {
  //   if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
  //     message.error("Only audio and video files are allowed!");
  //     return Upload.LIST_IGNORE; // Ignore the file if it's not valid
  //   }

  //   console.log(mediaFiles)

  //   return false; // Prevent default upload behavior
  // };

  // const handleDescriptionChange = (index, value) => {
  //   const updatedFiles = [...mediaFiles];
  //   updatedFiles[index].description = value;
  //   setMediaFiles(updatedFiles);
  // };


  // Handle file addition and saving file paths
  const onMediaChange = ({ file }) => {
    try {
      // Create a temporary URL for previewing the selected file
      const fileUrl = URL.createObjectURL(file); // Use the file object directly
      setMediaFiles((prev) => [
        ...prev,
        {
          link: fileUrl, // Temporary URL for playback/preview
          title: file.name || `${translations.talent.newMedia}`,
          description: `${translations.talent.descriptionFor} ${file.name}` || `${translations.talent.newMediaDescription}`,
          file, // Save the raw file object for uploading later
          type: file.type.startsWith("audio/") ? 'audio' : (file.type.startsWith("video/") ? 'video' : 'unknown')
        },
      ]);
    } catch (error) {
      // console.error("Failed to create object URL:", error);
      message.error(`${translations.talent.fileError}`);
    }
  };



  // Handle file validation (only audio or video files)
  const handleFileUpload = (file) => {
    if (mediaFiles.length > 4) {
      message.error(`${translations.talent.maxUploads}`);
      return; // Ignore invalid files
    }
    if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
      message.error(`${translations.talent.onlyAudioVideo}`);
      return Upload.LIST_IGNORE; // Ignore invalid files
    }
    return false; // Prevent auto-upload
  };

  // Remove file from the list
  const handleRemoveFile = (index) => {
    const updatedFiles = [...mediaFiles];
    updatedFiles.splice(index, 1);
    setMediaFiles(updatedFiles);
  };


  // Handle description change
  const handleDescriptionChange = (index, value) => {
    const updatedFiles = [...mediaFiles];
    updatedFiles[index].description = value;
    setMediaFiles(updatedFiles);
  };

  // const handleSubmit = (values) => {
  //   startDate: startDate ? startDate.valueOf() : null,

  //   console.log('Form Values:', values);
  //   message.success('Form submitted successfully!');
  // };

  const getExtension = (file) => {
    const fileName = file.name;
    const extension = fileName.split('.').pop(); // Gets the part after the last dot
    return extension;
  };

  const handleSubmit = (values) => {

    form
      .validateFields()
      .then(async () => {


        // Clean up the form data by removing undefined values
        const cleanedData = {
          ...values,
          artistBio: values.artistBio || '', // Ensure it's not undefined
          whyMusic: values.whyMusic || '', // Ensure it's not undefined
          socialMedia: values.socialMedia || '', // Ensure it's not undefined
        };

        // Collect the promises for uploading media files
        let uploadPromises = [];

        if (personalPic) {
          const personalPicUpload = uploadFile(personalPic.originFileObj, getExtension(personalPic.originFileObj), 'personalPics', (progress) => {
            setUploadProgress((prev) => ({ ...prev, personalPic: progress }));
          });
          uploadPromises.push(personalPicUpload);
        }

        if (mediaFiles.length > 0) {
          mediaFiles.forEach((media, index) => {
            const mediaFileUpload = uploadFile(media.file, getExtension(media.file), 'mediaFiles', (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                mediaFiles: { ...prev.mediaFiles, [index]: progress },
              }));
            });
            uploadPromises.push(mediaFileUpload);
          });
        }

        if (recordings.length > 0) {
          recordings.forEach((recording, index) => {
            const recordingUpload = uploadFile(recording.file, 'wav', 'recordings', (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                recordings: { ...prev.recordings, [index]: progress },
              }));
            });
            uploadPromises.push(recordingUpload);
          });
        }

        try {
          setUploading(true);

          // Wait for all uploads to finish
          const uploadResults = await Promise.all(uploadPromises);

          setUploading(false);

          // Prepare the submission data with the download URLs
          const submissionData = {
            ...cleanedData,

            // Handle personalPic assignment
            personalPic: personalPic
              ? {
                url: uploadResults[0],
              }
              : {},

            // Handle mediaFiles if they exist
            mediaFiles: mediaFiles.length > 0
              ? mediaFiles.map((file, index) => ({
                url: uploadResults[personalPic ? 1 + index : index], // Adjusted indexing based on personalPic
                title: file.title || `${translations.talent.defaultTitle}`,
                description: file.description || `${translations.talent.defaultDescription}`,
                type: file.type
              }))
              : [],

            // Handle recordings if they exist
            recordings: recordings.length > 0
              ? recordings.map((recording, index) => ({
                url: uploadResults[personalPic ? 1 + mediaFiles.length + index : mediaFiles.length + index], // Adjusted indexing based on personalPic
                title: recording.title || `${translations.talent.defaultTitle}`,
                description: recording.description || `${translations.talent.defaultDescription}`
              }))
              : [],
          };


          // Dispatch the submission data
          dispatch(talentSearchSubmissions(submissionData));
        } catch (error) {
          setUploading(false);

          // console.error('Error uploading files: ', error);
        }


      })
      .catch((errorInfo) => {
        // Scroll to the first error field smoothly
        form.scrollToField(errorInfo.errorFields[0].name);
      });

  };

  // const handleFileChange = (info) => {
  //   if (info.fileList) {
  //     setMediaFiles(info.fileList.map((file) => file.originFileObj));
  //   }
  // };

  // if (auth.currentUser) {
  //   // if (user) {
  //   // User logged in
  //   return <Navigate to="/dashboard" />;
  // }

  return (
    <div style={{ background: 'linear-gradient(135deg, #059941, #2f3589)', padding: '20px' }}>

      <Helmet>
        <title>{isSwahili ? "Jiunge na Sanecka Brand" : "Join Sanecka Brand"}</title>
        <meta
          name="description"
          content={isSwahili
            ? "Omba kujiunga na Sanecka Brand. Wasanii wanaweza kupakia maelezo yao, muziki wao, au hata kurekodi sampuli moja kwa moja kwa ajili ya mapitio."
            : "Apply to join Sanecka Brand. Artists can upload their details, showcase their music, or even record a sample directly for review."}
        />
        <meta
          name="keywords"
          content="music talent, artist discovery, new artists, talent search, Sanecka Brand, upload music, artist submission"
        />
        <meta
          property="og:title"
          content={isSwahili ? "Jiunge na Sanecka Brand" : "Join Sanecka Brand"}
        />
        <meta
          property="og:description"
          content={isSwahili
            ? "Sanecka Brand inatafuta vipaji vipya vya muziki. Wasanii wanaweza kutuma maombi kwa kupakia kazi zao au kurekodi sampuli moja kwa moja kwa ajili ya mapitio."
            : "Sanecka Brand is always in search for the best emerging musical talents. Artists can apply by uploading their work or recording a sample directly for review."}
        />
      </Helmet>

      <Title level={2} style={{ color: '#fff', textAlign: 'center', padding: 20 }}>
        {translations.talent.title}
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{
          maxWidth: '800px', margin: '0 auto',
          padding: '2rem',
          backgroundColor: '#fff',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #ddd',
        }}
        scrollToFirstError
      >
        {/* Personal Details Section */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={`${translations.talent.name}`}
              name="artistName"
              rules={[{ required: true, message: `${translations.talent.nameError}` }]}
            >
              <Input
                placeholder={`${translations.talent.namePlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={`${translations.talent.phoneNumber}`} name="phoneNumber" rules={[{ required: true, message: `${translations.talent.phoneNumberError}` }]}>
              <Input
                prefix={<PhoneOutlined />}
                placeholder={`${translations.talent.phoneNumberPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Gender Select */}
          <Col xs={24} sm={12}>
            <Form.Item
              label={`${translations.talent.gender}`}
              name="gender"
              rules={[{ required: true, message: `${translations.talent.genderError}` }]}
            >
              <Select
                placeholder={`${translations.talent.genderPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              >
                <Option value="male">{translations.talent.male}</Option>
                <Option value="female">{translations.talent.female}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={`${translations.talent.dob}`} name="dob" rules={[{ required: true, message: `${translations.talent.dobError}` }]}>
              <Input
                type="date"
                placeholder={`${translations.talent.dobPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>

        </Row>

        {/* Address Details Section */}
        <Row gutter={16}>


          {/* Nationality Input */}
          <Col xs={24} sm={12} >
            <Form.Item
              label={`${translations.talent.nationality}`}
              name="nationality"
              rules={[{ required: true, message: `${translations.talent.nationalityError}` }]}
            >
              <Input
                placeholder={`${translations.talent.nationalityPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label={`${translations.talent.address}`} name="address" rules={[{ required: true, message: `${translations.talent.addressError}` }]}>
              <Input
                placeholder={`${translations.talent.addressPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>

        </Row>

        <Row gutter={16}>

          <Col xs={24} sm={12}>
            <Form.Item label={`${translations.talent.city}`} name="city" rules={[{ required: true, message: `${translations.talent.cityError}` }]}>
              <Input
                placeholder={`${translations.talent.cityPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label={`${translations.talent.socialMedia}`} name="socialMedia">
              <Input
                prefix={<LinkOutlined />}
                placeholder={`${translations.talent.socialMediaPlaceholder}`}
                disabled={uploading || submitting} // Disable submit while loading
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Personal Image Upload */}
        <Form.Item label={`${translations.talent.uploadPPic}`}>
          {!personalPic && (

            <Upload
              name="personalPic"
              listType="picture"
              showUploadList={false}
              onChange={onPersonalPicChange}
              // beforeUpload={(file) => file.type.startsWith('image/')}
              beforeUpload={(file) => {
                if (!file.type.startsWith("image/")) {
                  // message.error(`${translations.talent.imageOnly}`);
                  return false;
                }
                return true; // Allow upload to trigger onChange
              }}
            >
              <Button
                icon={<UserOutlined />}
                style={{ backgroundColor: '#059941', color: '#fff' }}
                disabled={uploading || submitting} // Disable submit while loading
              >
                {translations.talent.uploadPic}
              </Button>
            </Upload>
          )}
          {personalPic && (
            <div style={{ marginTop: 16 }}>
              <img
                src={URL.createObjectURL(personalPic.originFileObj)}
                alt="Personal Pic"
                style={{ width: "150px", borderRadius: "4px" }}
              />
              <div>
                <Button danger onClick={() => setPersonalPic(null)} icon={<DeleteOutlined />}>Remove</Button>
              </div>
            </div>
          )}
          {uploading &&
            uploadProgress.personalPic > 0 && (
              <Progress percent={uploadProgress.personalPic} />
            )}
        </Form.Item>

        {/* Artist Bio */}
        <Form.Item label={`${translations.talent.bio}`} name="artistBio">
          <Input.TextArea
            rows={4}
            placeholder={`${translations.talent.bioPlaceholder}`}
            disabled={uploading || submitting} // Disable submit while loading
          />
        </Form.Item>

        {/* Why Choose Music Section */}
        <Form.Item label={`${translations.talent.whyMusic}`} name="whyMusic">
          <Input.TextArea
            rows={4}
            placeholder={`${translations.talent.whyMusicPlaceholder}`}
            disabled={uploading || submitting} // Disable submit while loading
          />
        </Form.Item>

        {/* Media Section */}
        <Form.Item label={`${translations.talent.uploadMusic}`}>
          <Upload
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)} // Simulate upload success
            fileList={[]}
            onChange={onMediaChange}
            beforeUpload={handleFileUpload}
            multiple
            showUploadList={false} // Disable default file list
          >
            <Button
              icon={<BiUpload />}
              style={{ backgroundColor: "#059941", color: "#fff" }}
              disabled={uploading || submitting} // Disable submit while loading
            >
              {translations.talent.uploadMedia}
            </Button>
          </Upload>

          {/* Display uploaded media files */}
          {mediaFiles.length > 0 && (
            <List
              style={{ marginTop: 16, marginBottom: 16 }}
              bordered
              dataSource={mediaFiles}
              renderItem={(file, index) => (
                <List.Item
                  actions={[
                    // Play button, disabled during upload
                    <Button
                      icon={<PlayCircleOutlined />}
                      onClick={() => window.open(file.link, "_blank")} // Play media file
                      type="link"
                      disabled={uploading || submitting}
                    >
                      {translations.talent.play}
                    </Button>,

                    // Remove button, disabled during upload
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFile(index)} // Remove file
                      type="link"
                      danger
                      disabled={uploading || submitting}
                    >
                      {translations.talent.remove}
                    </Button>,
                  ]}
                >
                  <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Text strong>{file.title}</Text>
                    <Form.Item>
                      <Input
                        placeholder={`${translations.talent.description}`}
                        value={file.description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        disabled={uploading || submitting} // Disable editing during upload
                      />
                    </Form.Item>

                    {/* Upload progress for each media file */}
                    {uploadProgress.mediaFiles[index] > 0 && (
                      <Progress
                        percent={uploadProgress.mediaFiles[index]}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </div>
                </List.Item>
              )}
            />
          )}

        </Form.Item>



        <Form.Item label={`${translations.talent.acapella}`}>
          <div>
            {/* Start/Stop Recording Button */}
            {status === "recording" ? (
              <Button
                icon={<BiMicrophoneOff />}
                onClick={stopRecording}
                style={{ backgroundColor: "#2f3589", color: "#fff" }}
              >
                {translations.talent.stop}
              </Button>
            ) : (
              <Button
                icon={<BiMicrophone />}
                onClick={handleStartRecording}
                style={{ backgroundColor: "#059941", color: "#fff" }}
                disabled={uploading || submitting} // Disable submit while loading
              >
                {translations.talent.start}
              </Button>
            )}
          </div>

          {/* List of Recorded Audio Files */}
          {recordings.length > 0 && (
            <List
              style={{ marginTop: 16, marginBottom: 16 }}
              bordered
              dataSource={recordings}
              renderItem={(recording, index) => (
                <List.Item
                  actions={[
                    // Play button, disabled during upload
                    <Button
                      icon={<PlayCircleOutlined />}
                      onClick={() => window.open(recording.link, "_blank")}
                      type="link"
                      disabled={uploading || submitting}
                    >
                      {translations.talent.play}
                    </Button>,

                    // Remove button, disabled during upload
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveRecording(index)}
                      type="link"
                      danger
                      disabled={uploading || submitting}
                    >
                      {translations.talent.remove}
                    </Button>,
                  ]}
                >
                  {/* Recording description and progress */}
                  <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Text strong>{recording.title}</Text>
                    <Form.Item>
                      <Input
                        placeholder={`${translations.talent.description}`}
                        value={recording.description}
                        onChange={(e) =>
                          handleRecordingDescriptionChange(index, e.target.value)
                        }
                        disabled={uploading || submitting} // Disable editing during upload
                      />
                    </Form.Item>

                    {/* Upload progress for each file */}
                    {uploadProgress.recordings[index] > 0 && (
                      <Progress
                        percent={uploadProgress.recordings[index]}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </div>
                </List.Item>
              )}
            />
          )}

        </Form.Item>




        {/* Submit Button */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            style={{ backgroundColor: '#2f3589', color: '#fff' }}
            disabled={uploading || submitting || status === "recording"} // Disable submit while loading
          >
            {(uploading || submitting) ? <Spin /> : `${translations.talent.send}`}
          </Button>
          {(uploading || submitting) &&
            <div style={{ marginTop: 5, marginBottom: 5 }}>
              <Progress
                percent={uploading ? 30 : 75}
                style={{ marginTop: 5, marginBottom: 5 }}
              />
              <Text type="secondary" style={{ display: "block", textAlign: "center" }}>
                {uploading ? `${translations.talent.uploading}` : `${translations.talent.finalizing}`}
              </Text>
            </div>
          }
          {success &&
            <Alert
              message={`${translations.talent.success}`}
              type="success"
              showIcon
              description={`${translations.talent.successDescription}`}
              style={{ marginTop: 5, marginBottom: 5 }}
              closable
            />
          }
          {error &&
            <Alert
              message={`${translations.talent.error}`}
              type="error"
              showIcon
              description={`${translations.talent.error}: ${error}`}
              style={{ marginTop: 5, marginBottom: 5 }}
              closable
            />
          }
        </Form.Item>
      </Form>
    </div>
  );
};

export default TalentSearch;
